import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import styled, { createGlobalStyle } from 'styled-components';

import { Link } from '@sorare/routing';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Auction } from '@sorare/core/src/atoms/icons/Auction';
import { iconMaximize } from '@sorare/core/src/atoms/icons/system/iconMaximize.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { VerticalDivider } from '@sorare/core/src/atoms/ui/Divider';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { MY_SORARE_PURCHASES_AUCTIONS } from '@sorare/core/src/constants/__generated__/routes';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thickMaterial } from '@sorare/core/src/style/utils';

import { AuctionBidItem } from '#src/components/auction/AuctionBidItem/index.tsx';
import { useMyBidsPictureInPictureContext } from '#src/contexts/myBidsPictureInPicture/index.ts';
import { useIgnoredBids } from '#src/hooks/auctions/useIgnoredBids.ts';

import type { MyBidsBanner_auctionConnection } from './__generated__/index.graphql.ts';

const MY_BIDS_BANNER_HEIGHT = 80;

export const MyBidsBannerWrapper = styled(Horizontal)`
  ${thickMaterial}
  position: sticky;
  bottom: 0;
  height: ${MY_BIDS_BANNER_HEIGHT}px;
  overflow: auto;
  border-top: 1px solid var(--c-nd-100);
`;

const PinnedBannerHeightVar = createGlobalStyle`
  :root {
    --my-bids-banner-height: ${MY_BIDS_BANNER_HEIGHT}px;
  }
`;
const IconContainer = styled(Auction)`
  width: 40px;
  height: 40px;
  background-color: var(--c-nd-150-opaque);
  border-radius: 50%;
  padding: var(--unit);
`;
const LiveCount = styled(Horizontal).attrs({ gap: 1, center: true })`
  width: 40px;
  background-color: var(--c-nd-150-opaque);
  border-radius: 20px;
  margin-top: calc(var(--unit) * -2);
`;
const PartialDivider = styled(VerticalDivider)`
  background-color: var(--c-nd-100);
  height: 50%;
  margin: 0 var(--unit);
  @media ${tabletAndAbove} {
    margin: 0 var(--intermediate-unit);
  }
`;
const StartIcon = styled(Vertical)`
  padding: 0 var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const Auctions = styled(Horizontal).attrs({ gap: 1 })`
  padding: var(--unit);
  @media ${tabletAndAbove} {
    padding: var(--intermediate-unit);
  }
  height: 100%;
`;
const MySorareLink = styled(Link)`
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;
const PopOutButton = styled(IconButton)`
  position: sticky;
  right: var(--unit);
  margin-left: auto;
  align-self: center;
  flex: none;
`;

type Props = {
  auctions: Nullable<MyBidsBanner_auctionConnection>;
  loading: boolean;
  loadMore: (
    loadMore: boolean,
    options: { after: string | undefined | null }
  ) => void;
};

export const MyBidsBanner = ({ auctions, loading, loadMore }: Props) => {
  const { formatMessage } = useIntl();
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const { ignoredBids, ignoreBid } = useIgnoredBids();
  const {
    supported: pictureInPictureSupported,
    open: pictureInPictureOpen,
    requestOpen: openPictureInPicture,
  } = useMyBidsPictureInPictureContext();

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: auctions?.pageInfo.endCursor,
      });
    },
    !!auctions?.pageInfo.hasNextPage,
    loading
  );
  const auctionsToShow = auctions?.nodes.filter(
    auction => !ignoredBids.includes(auction.id)
  );

  // The floating window already shows these bids.
  if (pictureInPictureOpen) return null;
  if (!auctionsToShow?.length) return null;

  return (
    <ConditionalWrapper
      wrap={!isTabletOrAbove}
      Wrapper={Portal}
      props={{
        id: 'above-bottom-bar-portal',
        style: {
          order: '99',
        },
      }}
    >
      {isTabletOrAbove ? <PinnedBannerHeightVar /> : null}
      <MyBidsBannerWrapper>
        <MySorareLink to={generatePath(MY_SORARE_PURCHASES_AUCTIONS)}>
          <StartIcon>
            <IconContainer />
            <LiveCount>
              <LiveDot size="sm" />
              <BodyS bold>{auctions?.totalCount}</BodyS>
            </LiveCount>
          </StartIcon>
        </MySorareLink>
        <Auctions>
          {auctionsToShow.map((auction, index) => (
            <Fragment key={auction.id}>
              {index > 0 && <PartialDivider />}
              <AuctionBidItem
                auction={auction}
                ignoreBid={() => ignoreBid(auction.id)}
              />
            </Fragment>
          ))}
        </Auctions>
        <InfiniteScrollLoader />
        {pictureInPictureSupported && isTabletOrAbove ? (
          <PopOutButton
            size="small"
            color="transparent"
            icon={iconMaximize}
            onClick={openPictureInPicture}
            aria-label={formatMessage({
              defaultMessage: 'Open in a floating window',
            })}
          />
        ) : null}
      </MyBidsBannerWrapper>
    </ConditionalWrapper>
  );
};

MyBidsBanner.fragments = {
  auctions: gql`
    fragment MyBidsBanner_auctionConnection on TokenAuctionConnection {
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
      nodes {
        id
        ...AuctionBidItem_tokenAuction
      }
    }
    ${AuctionBidItem.fragments.auction}
  ` as TypedDocumentNode<MyBidsBanner_auctionConnection>,
};
